// Biology → Section 3: Plants and Ecosystems (KS3, sourced from the
// Grade9Ready science PDF). Each topic follows the rich-content template
// established in biologySection1.js / biologySection2.js. Authored topics
// from the PDF pipeline (`authoredTopics.json`) replace any topic with a
// matching id at runtime.
import authored from './authoredTopics.json';

const AUTHORED_KEY = 'biology/plants-and-ecosystems';
const AUTHORED = (authored && authored[AUTHORED_KEY]) || [];
const AUTHORED_BY_ID = Object.fromEntries(AUTHORED.map((t) => [t.id, t]));

// =====================================================================
// 3.1  PHOTOSYNTHESIS
// =====================================================================
const PHOTOSYNTHESIS_TOPIC = {
  id: 'bio-3.1',
  title: 'Photosynthesis',
  slug: 'photosynthesis',
  pageRef: 'Section 3 · Topic 1',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What is photosynthesis?',
        body:
          'Photosynthesis is how green plants make their own food. They take in carbon dioxide from the air through tiny pores called stomata, water from the soil through their roots, and trap energy from sunlight using chlorophyll (the green pigment in leaves). The plant uses this energy to convert CO₂ and water into glucose (a sugar) and oxygen.',
        check: {
          question: 'Which gas do plants take IN during photosynthesis?',
          options: ['Oxygen', 'Carbon dioxide', 'Nitrogen', 'Hydrogen'],
          answer: 'Carbon dioxide',
          explainer: 'Plants take in CO₂ and release O₂ — the opposite of what we do when we breathe.',
        },
      },
      {
        heading: 'The word and symbol equations',
        body:
          'You must learn both equations. Word: carbon dioxide + water → glucose + oxygen (light + chlorophyll needed). Symbol: 6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂. The light isn\u2019t a reactant — it\u2019s the energy source. Chlorophyll isn\u2019t used up either — it\u2019s a catalyst.',
        check: {
          question: 'Which TWO substances are produced in photosynthesis?',
          options: ['CO₂ and water', 'Glucose and oxygen', 'Glucose and CO₂', 'Oxygen and water'],
          answer: 'Glucose and oxygen',
          explainer: 'Glucose is the food (used for energy or stored as starch). Oxygen is released as a waste product.',
        },
      },
      {
        heading: 'Where it happens — the leaf',
        body:
          'Most photosynthesis takes place in the leaves. Leaves are wide and flat (large surface area for light), thin (so gases diffuse quickly), and packed with chloroplasts in the palisade layer near the top. Stomata on the underside open to let CO₂ in and O₂ out. Veins (xylem and phloem) carry water in and glucose out.',
        check: {
          question: 'Which structure on a leaf lets gases enter and leave?',
          options: ['Cuticle', 'Stomata', 'Xylem', 'Phloem'],
          answer: 'Stomata',
          explainer: 'Stomata are tiny pores, mostly on the leaf underside, controlled by guard cells.',
        },
      },
    ],
    keyPoints: [
      'Photosynthesis: CO₂ + water → glucose + oxygen (light + chlorophyll)',
      'Symbol equation: 6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂',
      'Light = energy source; chlorophyll = catalyst (in chloroplasts)',
      'Leaves: large surface area, thin, full of chloroplasts',
      'Stomata let CO₂ in, O₂ out — controlled by guard cells',
      'Glucose is used for respiration or stored as starch',
    ],
    workedExamples: [
      {
        problem: 'A plant is kept in a dark cupboard for a week. Predict what happens to its starch reserves and why.',
        steps: [
          { step: 1, instruction: 'Identify the missing factor', working: 'No light → no photosynthesis' },
          { step: 2, instruction: 'What does the plant still do?', working: 'It still respires (uses glucose for energy) 24/7' },
          { step: 3, instruction: 'Where does the glucose come from?', working: 'From breaking down stored starch' },
        ],
        answer: 'Starch reserves fall because the plant respires but cannot photosynthesise to replace the glucose.',
        commonMistake: 'Saying the plant stops respiring in the dark — plants respire all the time, light or no light.',
      },
    ],
    whyItWorks:
      'Photosynthesis is the engine of every food chain. Almost all the energy in living things on Earth started as sunlight trapped by chlorophyll. Without photosynthesis, the atmosphere would have no oxygen and we wouldn\u2019t exist.',
  },
  glossary: [
    { term: 'Photosynthesis', definition: 'The process where plants make glucose using CO₂, water and light energy.' },
    { term: 'Chlorophyll', definition: 'The green pigment in leaves that absorbs light energy.' },
    { term: 'Chloroplast', definition: 'The leaf-cell organelle that contains chlorophyll.' },
    { term: 'Stomata', definition: 'Tiny pores on the leaf underside that let CO₂ in and O₂ out.' },
    { term: 'Glucose', definition: 'The sugar made in photosynthesis; used for energy or stored as starch.' },
    { term: 'Starch', definition: 'How plants store excess glucose for later.' },
    { term: 'Xylem', definition: 'Vessels that carry water from roots up to the leaves.' },
    { term: 'Phloem', definition: 'Vessels that carry glucose from leaves to the rest of the plant.' },
  ],
  examQuestion: {
    prompt:
      'A student tests a leaf for starch using iodine. The leaf turns blue-black. (a) What does the result show? (b) Write the word equation for photosynthesis. (c) Explain TWO ways a leaf is adapted for photosynthesis. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Blue-black with iodine = starch present, so leaf has been photosynthesising',
      'Carbon dioxide + water → glucose + oxygen (light/chlorophyll above arrow)',
      'Wide and flat = large surface area for light',
      'Thin = gases diffuse quickly to chloroplasts',
      'Packed with chloroplasts (palisade layer near top) for max light absorption',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.1.w1', type: 'text', question: 'Which green pigment traps light?', answer: 'chlorophyll', hint1: 'Starts with ch', hint2: '11 letters', hint3: 'Found in chloroplasts' },
      { id: 'bio-3.1.w2', type: 'choice', question: 'Photosynthesis produces…', options: ['CO₂', 'Oxygen', 'Nitrogen', 'Hydrogen'], answer: 'Oxygen', hint1: 'You breathe it in', hint2: 'O₂', hint3: 'Released through stomata' },
      { id: 'bio-3.1.w3', type: 'choice', question: 'Where does most photosynthesis happen?', options: ['Roots', 'Stem', 'Leaves', 'Flowers'], answer: 'Leaves', hint1: 'Wide and green', hint2: 'Most chloroplasts', hint3: '6 letters' },
    ],
    level1: [
      { id: 'bio-3.1.1', type: 'choice', question: 'Which gas is taken in by plants for photosynthesis?', options: ['Oxygen', 'Carbon dioxide', 'Hydrogen', 'Nitrogen'], answer: 'Carbon dioxide', hint1: 'CO₂', hint2: 'We breathe it out', hint3: 'Two words' },
      { id: 'bio-3.1.2', type: 'text', question: 'What is the food made in photosynthesis?', answer: 'glucose', hint1: 'A sugar', hint2: '7 letters', hint3: 'C₆H₁₂O₆' },
      { id: 'bio-3.1.3', type: 'choice', question: 'How is excess glucose stored in plants?', options: ['As fat', 'As protein', 'As starch', 'As oil'], answer: 'As starch', hint1: 'Tested with iodine', hint2: 'Long sugar chain', hint3: '6 letters' },
      { id: 'bio-3.1.4', type: 'text', question: 'Tiny pores on a leaf for gas exchange are called…', answer: 'stomata', hint1: 'Plural', hint2: '7 letters', hint3: 'Underside of leaf' },
    ],
    level2: [
      { id: 'bio-3.1.5', type: 'text', question: 'Word equation: carbon dioxide + ___ → glucose + oxygen.', answer: 'water', hint1: 'A liquid', hint2: 'H₂O', hint3: '5 letters' },
      { id: 'bio-3.1.6', type: 'choice', question: 'Which test confirms a leaf has been photosynthesising?', options: ['Benedict\u2019s', 'Iodine', 'Biuret', 'Limewater'], answer: 'Iodine', hint1: 'Tests for starch', hint2: 'Goes blue-black', hint3: '6 letters' },
      { id: 'bio-3.1.7', type: 'number', question: 'How many oxygen molecules are produced in the symbol equation?', answer: '6', hint1: 'Same as CO₂ taken in', hint2: 'Single digit', hint3: '6O₂' },
    ],
    level3: [
      { id: 'bio-3.1.8', type: 'choice', question: 'Why is a palisade layer near the top of a leaf?', options: ['To stop water loss', 'To get the most light', 'To support the leaf', 'To carry water'], answer: 'To get the most light', hint1: 'Photosynthesis needs light', hint2: 'Top = closest to sun', hint3: 'Maximises absorption' },
      { id: 'bio-3.1.9', type: 'text', question: 'A plant in the dark uses up its starch — what process still happens to consume glucose?', answer: 'respiration', hint1: 'Releases energy from glucose', hint2: '11 letters', hint3: 'Happens 24/7' },
      { id: 'bio-3.1.10', type: 'choice', question: 'Which is a LIMITING factor of photosynthesis?', options: ['Light intensity', 'CO₂ concentration', 'Temperature', 'All of the above'], answer: 'All of the above', hint1: 'Anything in short supply slows it', hint2: 'More than one', hint3: 'A, B and C' },
    ],
  },
};

// =====================================================================
// 3.2  PLANT MINERALS
// =====================================================================
const PLANT_MINERALS_TOPIC = {
  id: 'bio-3.2',
  title: 'Plant Minerals',
  slug: 'plant-minerals',
  pageRef: 'Section 3 · Topic 2',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Why glucose isn\u2019t enough',
        body:
          'Photosynthesis makes glucose, but plants need more than just glucose to grow healthy leaves and seeds. They also need mineral ions from the soil, dissolved in water and absorbed through the roots. The three big ones are nitrates, phosphates and potassium.',
        check: {
          question: 'Where do plants get mineral ions from?',
          options: ['The air', 'Sunlight', 'The soil', 'Other plants'],
          answer: 'The soil',
          explainer: 'Roots absorb mineral ions dissolved in soil water.',
        },
      },
      {
        heading: 'The three key minerals',
        body:
          'Nitrates supply nitrogen, used to make amino acids and proteins (needed for growth). Phosphates supply phosphorus, needed for DNA and for healthy roots. Potassium helps make the chemicals needed in photosynthesis and respiration. Without enough of any of these, the plant shows clear deficiency symptoms.',
        check: {
          question: 'Which mineral is needed to make proteins?',
          options: ['Nitrate', 'Phosphate', 'Potassium', 'Iron'],
          answer: 'Nitrate',
          explainer: 'Nitrogen from nitrates is built into amino acids, then proteins.',
        },
      },
      {
        heading: 'Deficiency symptoms',
        body:
          'Lack of nitrate → stunted growth and yellow older leaves. Lack of phosphate → poor root growth and purple younger leaves. Lack of potassium → yellow leaves with dead patches and poor flowers/fruits. Farmers add fertiliser containing N, P and K to replace minerals taken up by crops.',
        check: {
          question: 'A plant with stunted growth and yellow leaves is most likely missing…',
          options: ['Phosphate', 'Nitrate', 'Potassium', 'Magnesium'],
          answer: 'Nitrate',
          explainer: 'No nitrate = no proteins = poor growth, plus chlorophyll breaks down (yellowing).',
        },
      },
    ],
    keyPoints: [
      'Plants absorb mineral ions through roots, dissolved in water',
      'Nitrate → amino acids and proteins (growth)',
      'Phosphate → DNA and root growth',
      'Potassium → photosynthesis and respiration enzymes; flowers/fruit',
      'NPK fertilisers replace lost minerals in farmed soil',
    ],
    workedExamples: [
      {
        problem: 'A farmer notices their wheat plants are stunted with yellow leaves. Suggest the cause and one solution.',
        steps: [
          { step: 1, instruction: 'Identify the symptom', working: 'Stunted + yellow leaves = classic nitrate deficiency' },
          { step: 2, instruction: 'Why?', working: 'Nitrate needed for protein → no nitrate = no growth' },
          { step: 3, instruction: 'Solution', working: 'Add NPK fertiliser to the soil' },
        ],
        answer: 'Nitrate deficiency. Apply nitrogen-rich (NPK) fertiliser.',
        commonMistake: 'Confusing nitrate (growth) with potassium (flowers/fruit).',
      },
    ],
    whyItWorks:
      'A plant is a chemical factory. Glucose alone can\u2019t build proteins, DNA or chlorophyll — it needs nitrogen, phosphorus and potassium too. That\u2019s why bagged compost lists "NPK" on the label.',
  },
  glossary: [
    { term: 'Mineral ion', definition: 'A dissolved nutrient (e.g. nitrate) absorbed by plant roots.' },
    { term: 'Nitrate', definition: 'A nitrogen-containing ion needed to make amino acids and proteins.' },
    { term: 'Phosphate', definition: 'A phosphorus-containing ion needed for DNA and root growth.' },
    { term: 'Potassium', definition: 'A mineral ion needed for enzymes in photosynthesis and respiration.' },
    { term: 'Deficiency', definition: 'Symptoms shown when a plant cannot get enough of a mineral.' },
    { term: 'Fertiliser', definition: 'A substance added to soil (often NPK) to replace lost minerals.' },
  ],
  examQuestion: {
    prompt:
      'A student grows two cress seedlings. Plant A is in normal soil; Plant B is in soil with no nitrate. After 2 weeks, Plant B is shorter with yellow leaves. (a) Explain WHY Plant B is shorter. (b) Suggest TWO other minerals the plant also needs and what each is for. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'No nitrate → cannot make amino acids',
      'No amino acids → cannot make proteins for growth',
      'Phosphate — for DNA / root growth',
      'Potassium — for enzymes in photosynthesis / flowers and fruit',
      'Plus: chlorophyll cannot be made → yellow leaves',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.2.w1', type: 'text', question: 'Which part of a plant absorbs minerals?', answer: 'roots', hint1: 'Underground', hint2: '5 letters', hint3: 'Plural' },
      { id: 'bio-3.2.w2', type: 'choice', question: 'Which letter in NPK fertiliser stands for potassium?', options: ['N', 'P', 'K', 'None'], answer: 'K', hint1: 'Latin name kalium', hint2: 'Single letter', hint3: 'Last letter of NPK' },
      { id: 'bio-3.2.w3', type: 'choice', question: 'Minerals enter roots dissolved in…', options: ['Air', 'Oil', 'Water', 'Sunlight'], answer: 'Water', hint1: 'Liquid', hint2: '5 letters', hint3: 'H₂O' },
    ],
    level1: [
      { id: 'bio-3.2.1', type: 'text', question: 'Which mineral ion makes proteins?', answer: 'nitrate', hint1: 'NPK first letter', hint2: '7 letters', hint3: 'Contains nitrogen' },
      { id: 'bio-3.2.2', type: 'choice', question: 'Phosphate is mainly needed for…', options: ['Photosynthesis enzymes', 'DNA and root growth', 'Flowers', 'Cell wall'], answer: 'DNA and root growth', hint1: 'P in NPK', hint2: 'Building genetic material', hint3: 'Strong roots' },
      { id: 'bio-3.2.3', type: 'choice', question: 'Yellow older leaves and stunted growth indicates a lack of…', options: ['Phosphate', 'Potassium', 'Nitrate', 'Calcium'], answer: 'Nitrate', hint1: 'Needed for proteins', hint2: 'No protein = no growth', hint3: 'NPK first letter' },
    ],
    level2: [
      { id: 'bio-3.2.4', type: 'text', question: 'A purple tinge on young leaves often shows a lack of which mineral?', answer: 'phosphate', hint1: '9 letters', hint2: 'Linked to DNA and roots', hint3: 'P in NPK' },
      { id: 'bio-3.2.5', type: 'choice', question: 'Why do farmers rotate crops?', options: ['To pretty the field', 'To stop the same minerals being used up year after year', 'To save fuel', 'To trick pests with colour'], answer: 'To stop the same minerals being used up year after year', hint1: 'Different crops use different minerals', hint2: 'Soil exhaustion', hint3: 'Sustainability' },
    ],
    level3: [
      { id: 'bio-3.2.6', type: 'choice', question: 'Why might a plant with poor flowers benefit from extra potassium?', options: ['Builds more chlorophyll', 'Helps enzymes that produce flowers/fruit', 'Replaces water', 'Grows new roots'], answer: 'Helps enzymes that produce flowers/fruit', hint1: 'K is the F&F mineral', hint2: 'Tomato feed is K rich', hint3: 'Reproductive growth' },
      { id: 'bio-3.2.7', type: 'text', question: 'NPK stands for nitrogen, phosphorus and ___.', answer: 'potassium', hint1: 'Symbol K', hint2: '9 letters', hint3: 'Found in bananas' },
    ],
  },
};

// =====================================================================
// 3.3  PLANT REPRODUCTION
// =====================================================================
const PLANT_REPRO_TOPIC = {
  id: 'bio-3.3',
  title: 'Plant Reproduction',
  slug: 'plant-reproduction',
  pageRef: 'Section 3 · Topic 3',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Parts of a flower',
        body:
          'Flowers are a plant\u2019s sex organs. The male part is the stamen (anther + filament); the anther makes pollen. The female part is the carpel (stigma, style, ovary). The ovary contains ovules. Petals (often coloured) attract pollinators; sepals protect the bud before it opens.',
        check: {
          question: 'Which part of a flower produces pollen?',
          options: ['Stigma', 'Anther', 'Ovary', 'Petal'],
          answer: 'Anther',
          explainer: 'The anther sits on top of the filament — together they make up the stamen (male part).',
        },
      },
      {
        heading: 'Pollination',
        body:
          'Pollination is the transfer of pollen from anther to stigma. Insect-pollinated flowers are usually large, brightly coloured and scented, with sticky pollen and a sticky stigma. Wind-pollinated flowers are small, dull, often without petals; they hang outside the leaves with feathery stigmas and produce huge amounts of light, smooth pollen.',
        check: {
          question: 'Which feature suggests a flower is wind-pollinated?',
          options: ['Bright petals', 'Strong scent', 'Feathery stigma', 'Sticky pollen'],
          answer: 'Feathery stigma',
          explainer: 'A feathery stigma catches pollen as it drifts by on the wind.',
        },
      },
      {
        heading: 'Fertilisation in plants',
        body:
          'Once pollen lands on a stigma of the same species, a pollen tube grows down the style to an ovule in the ovary. The male gamete travels down the tube and fuses with the ovule (female gamete). This is fertilisation. The ovule then develops into a seed; the ovary develops into the fruit around it.',
        check: {
          question: 'After fertilisation, the ovary develops into the…',
          options: ['Petal', 'Seed', 'Fruit', 'Stem'],
          answer: 'Fruit',
          explainer: 'Fruit is the swollen ovary that protects and helps disperse the seeds inside.',
        },
      },
    ],
    keyPoints: [
      'Stamen (male) = anther + filament; anther makes pollen',
      'Carpel (female) = stigma, style, ovary (contains ovules)',
      'Pollination: pollen anther → stigma',
      'Insect flowers: bright, scented, sticky pollen',
      'Wind flowers: dull, feathery stigma, light pollen',
      'Fertilisation: pollen tube grows down style; gametes fuse',
      'Ovule → seed; ovary → fruit',
    ],
    workedExamples: [
      {
        problem: 'A flower is small, has dull petals, no scent, hangs in the wind, and has feathery stigmas. Decide whether it is insect- or wind-pollinated and justify with TWO reasons.',
        steps: [
          { step: 1, instruction: 'List the features', working: 'small, dull, no scent, feathery stigma' },
          { step: 2, instruction: 'Match to category', working: 'These are all wind-pollination adaptations' },
        ],
        answer: 'Wind-pollinated. Reasons: feathery stigma catches drifting pollen; no need for scent/colour to attract insects.',
        commonMistake: 'Calling it wind because it\u2019s small. The clincher is the feathery stigma + no scent.',
      },
    ],
    whyItWorks:
      'Flowers are pollination machines. Their shape, smell and colour are tuned over millions of years to either attract a pollinator or catch the wind. The flower that "wins" is the one whose pollen reaches the most stigmas of its own species.',
  },
  glossary: [
    { term: 'Stamen', definition: 'The male part of a flower (anther + filament).' },
    { term: 'Anther', definition: 'The part of the stamen that makes pollen.' },
    { term: 'Carpel', definition: 'The female part of a flower (stigma, style, ovary).' },
    { term: 'Stigma', definition: 'The sticky tip of the carpel where pollen lands.' },
    { term: 'Ovary', definition: 'The base of the carpel containing ovules; becomes the fruit after fertilisation.' },
    { term: 'Ovule', definition: 'The female gamete inside the ovary; becomes a seed after fertilisation.' },
    { term: 'Pollination', definition: 'Transfer of pollen from anther to stigma.' },
    { term: 'Fertilisation', definition: 'Fusion of male and female gametes to form a zygote.' },
    { term: 'Pollen tube', definition: 'A tube that grows from a pollen grain on a stigma down to an ovule.' },
  ],
  examQuestion: {
    prompt:
      'A flower has bright petals, a strong scent and sticky pollen. (a) Suggest its method of pollination and give TWO reasons. (b) Describe what happens between pollination and fertilisation in this flower. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Insect-pollinated',
      'Bright petals/scent attract insects',
      'Sticky pollen sticks to insect bodies',
      'Pollen lands on stigma → pollen tube grows down style to ovule',
      'Male gamete fuses with female gamete (ovule) — fertilisation',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.3.w1', type: 'text', question: 'The male part of a flower is called the…', answer: 'stamen', hint1: '6 letters', hint2: 'Anther + filament', hint3: 'Starts with s' },
      { id: 'bio-3.3.w2', type: 'choice', question: 'Which is the female part of a flower?', options: ['Stamen', 'Carpel', 'Petal', 'Sepal'], answer: 'Carpel', hint1: 'Has stigma, style, ovary', hint2: '6 letters', hint3: 'Starts with c' },
      { id: 'bio-3.3.w3', type: 'choice', question: 'Pollen is transferred from anther to…', options: ['Style', 'Stigma', 'Ovary', 'Sepal'], answer: 'Stigma', hint1: 'Sticky tip of carpel', hint2: 'On top of style', hint3: '6 letters' },
    ],
    level1: [
      { id: 'bio-3.3.1', type: 'choice', question: 'After fertilisation, the ovule becomes a…', options: ['Petal', 'Seed', 'Fruit', 'Stem'], answer: 'Seed', hint1: 'Inside the fruit', hint2: 'Can be planted', hint3: '4 letters' },
      { id: 'bio-3.3.2', type: 'text', question: 'Which feature of a wind flower catches drifting pollen?', answer: 'feathery stigma', hint1: 'Sticky tip', hint2: 'Two words', hint3: 'Like a feather' },
      { id: 'bio-3.3.3', type: 'choice', question: 'Insect-pollinated flowers tend to have…', options: ['Dull petals', 'Bright scented petals', 'No petals at all', 'Feathery stigmas'], answer: 'Bright scented petals', hint1: 'Attract insects', hint2: 'Visible from afar', hint3: 'Plus nectar' },
    ],
    level2: [
      { id: 'bio-3.3.4', type: 'text', question: 'A pollen grain grows a tube down through the ___ to reach the ovule.', answer: 'style', hint1: '5 letters', hint2: 'Connects stigma to ovary', hint3: 'Part of carpel' },
      { id: 'bio-3.3.5', type: 'choice', question: 'Why do wind flowers make so much pollen?', options: ['Larger pollen needed', 'Wind is unreliable, most pollen is wasted', 'Insects eat most of it', 'Pollen grows in clusters'], answer: 'Wind is unreliable, most pollen is wasted', hint1: 'Random delivery', hint2: 'Numbers game', hint3: 'Compensates for waste' },
    ],
    level3: [
      { id: 'bio-3.3.6', type: 'choice', question: 'Which is NOT a flower part?', options: ['Anther', 'Stigma', 'Villi', 'Ovary'], answer: 'Villi', hint1: 'Villi belong to gut', hint2: 'Not a plant term', hint3: 'Small intestine' },
      { id: 'bio-3.3.7', type: 'text', question: 'After fertilisation, the ovary becomes the ___ that protects seeds.', answer: 'fruit', hint1: '5 letters', hint2: 'Apple, tomato, etc.', hint3: 'Helps dispersal' },
    ],
  },
};

// =====================================================================
// 3.4  SEED DISPERSAL
// =====================================================================
const SEED_DISPERSAL_TOPIC = {
  id: 'bio-3.4',
  title: 'Seed Dispersal',
  slug: 'seed-dispersal',
  pageRef: 'Section 3 · Topic 4',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Why disperse?',
        body:
          'If all seeds dropped under the parent plant they\u2019d compete for the same light, water and minerals — and most would die. Dispersal moves seeds AWAY from the parent so they have a better chance of growing. Plants use four main methods: wind, water, animals (eaten or carried) and self-launching (explosive).',
        check: {
          question: 'Why do seeds need to be dispersed away from the parent plant?',
          options: ['To find sunlight', 'To avoid competition', 'To grow taller', 'To stay warm'],
          answer: 'To avoid competition',
          explainer: 'Without dispersal, every seed competes with the parent and other siblings for the same resources.',
        },
      },
      {
        heading: 'Wind, water and explosive dispersal',
        body:
          'Wind-dispersed seeds (e.g. dandelion, sycamore) are tiny, light, and have wings or parachutes so they\u2019re carried far. Water-dispersed seeds (e.g. coconut) are waterproof and float. Explosive (self) dispersal happens when seed pods dry out and snap open, flicking seeds away (e.g. peas, gorse).',
        check: {
          question: 'A sycamore seed has wing-like extensions. Method of dispersal?',
          options: ['Animal', 'Water', 'Wind', 'Explosive'],
          answer: 'Wind',
          explainer: 'The wings make it spin like a helicopter, slowing its fall and letting wind carry it further.',
        },
      },
      {
        heading: 'Animal dispersal',
        body:
          'Two ways: (1) Eaten — sweet, brightly coloured fruits are swallowed by animals; the seeds pass through the gut and come out in droppings, miles from the parent. The seed coat is tough so it survives stomach acid. (2) Carried — burrs and hooked seeds (e.g. cleavers, burdock) catch on fur or clothing and drop off later.',
        check: {
          question: 'Bramble fruits are sweet and brightly coloured. Why?',
          options: ['To photosynthesise', 'So animals eat them and disperse the seeds', 'To deter pests', 'To attract pollinators'],
          answer: 'So animals eat them and disperse the seeds',
          explainer: 'Bright colour and sweet flesh tempt animals; the tough seed survives the gut and is dispersed in droppings.',
        },
      },
    ],
    keyPoints: [
      'Dispersal reduces competition with parent and siblings',
      'Wind: light, wings/parachutes (dandelion, sycamore)',
      'Water: waterproof, floats (coconut)',
      'Animal (eaten): sweet, coloured fruits, tough seed coat',
      'Animal (carried): burrs/hooks catch on fur',
      'Self / explosive: pods snap open (peas, gorse)',
    ],
    workedExamples: [
      {
        problem: 'A seed has tiny hooks all over its surface. Suggest its method of dispersal and explain.',
        steps: [
          { step: 1, instruction: 'Identify the feature', working: 'Hooks → not light, not floaty, not edible' },
          { step: 2, instruction: 'Match', working: 'Hooks catch on animal fur' },
        ],
        answer: 'Animal dispersal (carried). Hooks attach the seed to passing animals\u2019 fur, which then drops it elsewhere.',
        commonMistake: 'Calling it animal-eaten — that needs a sweet fruit, not hooks.',
      },
    ],
    whyItWorks:
      'Each dispersal method matches the plant\u2019s habitat: coconuts ride ocean currents, dandelions ride breezes, brambles ride birds. Evolution shapes the seed to whatever vehicle is going past.',
  },
  glossary: [
    { term: 'Dispersal', definition: 'The spread of seeds away from the parent plant.' },
    { term: 'Burr', definition: 'A hooked or sticky seed/fruit that clings to fur.' },
    { term: 'Pod', definition: 'A dry fruit (like a pea) that splits open to release seeds.' },
    { term: 'Competition', definition: 'When organisms struggle for the same resource (light, water, minerals).' },
  ],
  examQuestion: {
    prompt:
      'A coconut is large, has a thick fibrous waterproof coat, and floats. (a) Identify its method of dispersal. (b) Explain how its features suit this method. (c) Why is dispersal important for the plant\u2019s survival? (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Water dispersal',
      'Waterproof coat protects seed from sea water',
      'Fibrous coat traps air → floats',
      'Reduces competition with parent for light/water/minerals',
      'Reaches new habitats / colonises islands',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.4.w1', type: 'text', question: 'Dandelion seeds are dispersed by…', answer: 'wind', hint1: '4 letters', hint2: 'Light, parachute', hint3: 'Blows them away' },
      { id: 'bio-3.4.w2', type: 'choice', question: 'A coconut is dispersed by…', options: ['Wind', 'Water', 'Animals', 'Explosion'], answer: 'Water', hint1: 'Floats', hint2: 'Salty travel', hint3: 'Tropical beaches' },
      { id: 'bio-3.4.w3', type: 'choice', question: 'Why do plants disperse seeds?', options: ['To grow taller', 'To avoid competition', 'To save energy', 'To reproduce sexually'], answer: 'To avoid competition', hint1: 'Less crowding', hint2: 'Resource sharing', hint3: 'New space' },
    ],
    level1: [
      { id: 'bio-3.4.1', type: 'text', question: 'Sycamore seeds spin like helicopters when dropped — they are dispersed by ___.', answer: 'wind', hint1: '4 letters', hint2: 'Air movement', hint3: 'Parachute / wings' },
      { id: 'bio-3.4.2', type: 'choice', question: 'Which is NOT a method of seed dispersal?', options: ['Wind', 'Water', 'Photosynthesis', 'Animals'], answer: 'Photosynthesis', hint1: 'Photosynthesis is for food', hint2: 'Not movement', hint3: 'Reactant: CO₂' },
      { id: 'bio-3.4.3', type: 'choice', question: 'Sweet, brightly coloured fruits are usually dispersed by…', options: ['Wind', 'Water', 'Animals (eaten)', 'Animals (carried)'], answer: 'Animals (eaten)', hint1: 'Tasty', hint2: 'Eaten then pooped', hint3: 'Berries, apples' },
    ],
    level2: [
      { id: 'bio-3.4.4', type: 'text', question: 'A pea pod dries out and snaps open. This dispersal method is called ___.', answer: 'explosive', hint1: '9 letters', hint2: 'Sudden launch', hint3: 'Self-dispersal' },
      { id: 'bio-3.4.5', type: 'choice', question: 'Why must seeds eaten by animals have tough coats?', options: ['To stay warm', 'To survive stomach acid and digestion', 'To photosynthesise', 'To absorb water'], answer: 'To survive stomach acid and digestion', hint1: 'Going through a gut', hint2: 'pH 2 in stomach', hint3: 'Protection' },
    ],
    level3: [
      { id: 'bio-3.4.6', type: 'choice', question: 'Why are wind-dispersed seeds usually small?', options: ['So they fit in pods', 'So they are light enough to be carried far', 'To save the plant energy', 'To attract birds'], answer: 'So they are light enough to be carried far', hint1: 'Less mass = more drift', hint2: 'Buoyancy in air', hint3: 'Lower terminal velocity' },
      { id: 'bio-3.4.7', type: 'text', question: 'Cleavers (sticky willy) cling to dog fur — this is animal dispersal by ___ .', answer: 'carrying', hint1: 'Not eaten', hint2: 'Hooks/burrs', hint3: 'Hitches a ride', altAnswers: ['carry', 'attachment'] },
    ],
  },
};

// =====================================================================
// 3.5  ECOSYSTEMS AND HABITATS
// =====================================================================
const ECOSYSTEMS_TOPIC = {
  id: 'bio-3.5',
  title: 'Ecosystems and Habitats',
  slug: 'ecosystems-and-habitats',
  pageRef: 'Section 3 · Topic 5',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Key vocabulary',
        body:
          'Habitat = the place an organism lives (e.g. a pond, an oak tree). Population = all the individuals of one species in a habitat. Community = all the populations (different species) in a habitat. Ecosystem = the community PLUS the non-living surroundings (light, soil, water, temperature).',
        check: {
          question: 'Which of these includes the non-living surroundings?',
          options: ['Habitat', 'Population', 'Community', 'Ecosystem'],
          answer: 'Ecosystem',
          explainer: 'Ecosystem = community (living) + abiotic factors (non-living).',
        },
      },
      {
        heading: 'Biotic and abiotic factors',
        body:
          'Biotic = living factors (food, predators, competition, disease). Abiotic = non-living factors (light intensity, temperature, water, oxygen, pH, mineral content). All these factors affect which species can live where. A polar bear couldn\u2019t survive in the desert because of the temperature; a cactus couldn\u2019t survive in a swamp because of waterlogged soil.',
        check: {
          question: 'Which is an abiotic factor?',
          options: ['Predator', 'Disease', 'Temperature', 'Food supply'],
          answer: 'Temperature',
          explainer: 'Abiotic = non-living. Temperature is purely physical.',
        },
      },
      {
        heading: 'Adaptations',
        body:
          'Organisms have features that help them survive in their habitat — these are adaptations. Polar bears have thick fur and white camouflage (cold + snow). Cacti have a thick waxy stem and spines (water-storage and defence). Earthworms are long and thin with smooth skin (moving through soil). Mismatched organisms die out — well-adapted ones thrive.',
        check: {
          question: 'A cactus stores water in its swollen stem. This is an example of…',
          options: ['Photosynthesis', 'Adaptation', 'Reproduction', 'Dispersal'],
          answer: 'Adaptation',
          explainer: 'A feature that helps an organism survive in its environment is an adaptation.',
        },
      },
    ],
    keyPoints: [
      'Habitat = place an organism lives',
      'Population = all of ONE species in a habitat',
      'Community = ALL species in a habitat',
      'Ecosystem = community + abiotic factors',
      'Biotic = living factors; Abiotic = non-living',
      'Adaptation = feature that helps an organism survive',
    ],
    workedExamples: [
      {
        problem: 'In a pond there are 30 frogs, 2000 tadpoles, and 50 dragonflies. How many populations? How many communities?',
        steps: [
          { step: 1, instruction: 'Count species', working: 'Frogs (1), tadpoles are baby frogs (same species), dragonflies (1) = 2 species' },
          { step: 2, instruction: 'Apply definitions', working: 'Each species is a population → 2 populations. All species together = 1 community' },
        ],
        answer: '2 populations, 1 community.',
        commonMistake: 'Counting tadpoles and frogs as different populations — they\u2019re the same species at different life stages.',
      },
    ],
    whyItWorks:
      'Ecology is about relationships. Every living thing is shaped by who else is in the habitat and what the environment is like. Change one factor — temperature, predator, food supply — and the whole web shifts.',
  },
  glossary: [
    { term: 'Habitat', definition: 'The place where an organism lives.' },
    { term: 'Population', definition: 'All the individuals of one species in a habitat.' },
    { term: 'Community', definition: 'All the populations (different species) living in a habitat.' },
    { term: 'Ecosystem', definition: 'A community plus the non-living factors of its environment.' },
    { term: 'Biotic', definition: 'Living factors (predators, food, disease).' },
    { term: 'Abiotic', definition: 'Non-living factors (light, temperature, water, pH).' },
    { term: 'Adaptation', definition: 'A feature that helps an organism survive in its habitat.' },
  ],
  examQuestion: {
    prompt:
      'A garden pond contains 50 frogs, 200 fish (one species), 1000 plants (one species) and many insects (3 species). (a) How many populations live in the pond? (b) Define ecosystem. (c) Suggest TWO abiotic factors that would affect the fish population. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      '6 populations (1 frog + 1 fish + 1 plant + 3 insect)',
      'Ecosystem = community of organisms PLUS abiotic factors',
      'Oxygen levels',
      'Water temperature / pH',
      'Light intensity / mineral content',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.5.w1', type: 'text', question: 'The place an organism lives is its ___ .', answer: 'habitat', hint1: '7 letters', hint2: 'Starts with h', hint3: 'Forest, pond, etc.' },
      { id: 'bio-3.5.w2', type: 'choice', question: 'Which is biotic?', options: ['pH', 'Predator', 'Light', 'Temperature'], answer: 'Predator', hint1: 'Living', hint2: 'Eats prey', hint3: 'Animal' },
      { id: 'bio-3.5.w3', type: 'choice', question: 'Which is abiotic?', options: ['Disease', 'Food', 'Water', 'Predator'], answer: 'Water', hint1: 'Non-living', hint2: 'A liquid', hint3: 'H₂O' },
    ],
    level1: [
      { id: 'bio-3.5.1', type: 'text', question: 'All the wood mice in a wood are called a ___ .', answer: 'population', hint1: '10 letters', hint2: 'Same species', hint3: 'Census term' },
      { id: 'bio-3.5.2', type: 'choice', question: 'Which best defines an ecosystem?', options: ['One species', 'Living things only', 'Community + abiotic factors', 'Habitat alone'], answer: 'Community + abiotic factors', hint1: 'Includes non-living', hint2: 'Most complete option', hint3: 'C' },
      { id: 'bio-3.5.3', type: 'text', question: 'A cactus has a thick waxy stem to reduce ___ loss.', answer: 'water', hint1: 'Found in desert', hint2: '5 letters', hint3: 'Liquid' },
    ],
    level2: [
      { id: 'bio-3.5.4', type: 'choice', question: 'A polar bear\u2019s white fur helps with…', options: ['Insulation', 'Camouflage in snow', 'Photosynthesis', 'Hunting in trees'], answer: 'Camouflage in snow', hint1: 'White on white', hint2: 'Hunting advantage', hint3: 'Avoid being seen' },
      { id: 'bio-3.5.5', type: 'choice', question: 'Which is most likely to limit fish numbers?', options: ['Cloud cover', 'Tree height', 'Oxygen in the water', 'Soil pH on land'], answer: 'Oxygen in the water', hint1: 'Fish breathe with gills', hint2: 'Dissolved O₂', hint3: 'Aquatic abiotic factor' },
    ],
    level3: [
      { id: 'bio-3.5.6', type: 'choice', question: 'In a meadow, all the rabbits, foxes and grasses together form a…', options: ['Habitat', 'Population', 'Community', 'Ecosystem'], answer: 'Community', hint1: 'All species, not abiotic', hint2: 'No mention of soil/light', hint3: 'Living only' },
      { id: 'bio-3.5.7', type: 'text', question: 'Earthworms have moist skin to allow gas exchange — this is an example of an ___ .', answer: 'adaptation', hint1: '10 letters', hint2: 'Useful feature', hint3: 'Helps survival' },
    ],
  },
};

// =====================================================================
// 3.6  FOOD CHAINS AND WEBS
// =====================================================================
const FOOD_CHAINS_TOPIC = {
  id: 'bio-3.6',
  title: 'Food Chains and Webs',
  slug: 'food-chains-and-webs',
  pageRef: 'Section 3 · Topic 6',
  difficulty: 'foundation',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Producers and consumers',
        body:
          'Energy enters every food chain through producers — green plants and algae that capture sunlight via photosynthesis. Animals are consumers: a primary consumer eats the producer (often a herbivore); a secondary consumer eats the primary; tertiary consumers eat secondary, and so on. Arrows in a food chain ALWAYS point in the direction energy flows (i.e. from prey to predator).',
        check: {
          question: 'In the chain grass → rabbit → fox, the rabbit is the…',
          options: ['Producer', 'Primary consumer', 'Secondary consumer', 'Decomposer'],
          answer: 'Primary consumer',
          explainer: 'Primary consumers eat the producer directly.',
        },
      },
      {
        heading: 'Predators, prey and food webs',
        body:
          'A predator hunts and eats another animal (its prey). Most habitats have many overlapping food chains — drawn together they form a food web. If one species is removed, the whole web shifts: predators of the lost species lose food; prey of the lost species may multiply because they\u2019re no longer eaten.',
        check: {
          question: 'If foxes are removed from grass → rabbit → fox, what happens to rabbit numbers (short term)?',
          options: ['Decrease', 'Stay the same', 'Increase', 'Become extinct'],
          answer: 'Increase',
          explainer: 'Without predators, rabbits multiply — at least until they run out of grass.',
        },
      },
      {
        heading: 'Energy is lost at each step',
        body:
          'Only ~10% of energy is passed on at each level — the rest is lost as heat (respiration), in waste (faeces and urine), or in parts not eaten (bones, fur). That\u2019s why food chains rarely have more than four or five levels: there isn\u2019t enough energy left to support another consumer.',
        check: {
          question: 'About what fraction of energy passes from one level to the next?',
          options: ['Almost all', '~50%', '~10%', 'Less than 1%'],
          answer: '~10%',
          explainer: '90% is lost as heat, waste, and uneaten parts.',
        },
      },
    ],
    keyPoints: [
      'Producer = plant (captures sunlight)',
      'Consumer levels: primary, secondary, tertiary',
      'Arrows point from prey → predator (direction of energy flow)',
      'Food web = many connected food chains',
      'Remove a species → cascading effects on web',
      '~90% of energy lost at each step (heat, waste, uneaten parts)',
    ],
    workedExamples: [
      {
        problem: 'In the chain grass → grasshopper → frog → snake, the grass holds 10000 J of energy. Estimate the energy available to the snake.',
        steps: [
          { step: 1, instruction: 'Apply 10% rule', working: '10000 × 0.1 = 1000 J at grasshopper' },
          { step: 2, instruction: 'Next level', working: '1000 × 0.1 = 100 J at frog' },
          { step: 3, instruction: 'Final level', working: '100 × 0.1 = 10 J at snake' },
        ],
        answer: '~10 J reaches the snake.',
        commonMistake: 'Subtracting 90% only once — energy loss happens at EVERY step.',
      },
    ],
    whyItWorks:
      'Food chains are essentially energy pipelines. Sun → plants → herbivores → carnivores. Because the pipeline is leaky (~90% loss per stage), the higher up you go, the fewer organisms there can be — which is why apex predators are rare.',
  },
  glossary: [
    { term: 'Producer', definition: 'An organism that makes its own food (usually a green plant) — the start of a food chain.' },
    { term: 'Consumer', definition: 'An organism that eats other organisms.' },
    { term: 'Primary consumer', definition: 'The first consumer in a food chain — eats the producer.' },
    { term: 'Predator', definition: 'An animal that hunts and eats other animals.' },
    { term: 'Prey', definition: 'An animal that is hunted and eaten by a predator.' },
    { term: 'Food web', definition: 'A network of overlapping food chains in a habitat.' },
    { term: 'Decomposer', definition: 'An organism (e.g. fungus, bacterium) that breaks down dead material.' },
  ],
  examQuestion: {
    prompt:
      'A food chain is: oak leaves → caterpillars → blue tits → sparrowhawks. (a) Name the producer. (b) Explain why there are far fewer sparrowhawks than caterpillars. (c) Predict the short-term effect on caterpillar numbers if all the blue tits died of disease. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Oak leaves (oak tree)',
      '~90% of energy is lost at each level',
      'so less energy is available higher up the chain',
      'Caterpillar numbers would increase',
      'Because their predator (blue tits) is gone',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.6.w1', type: 'text', question: 'The first organism in a food chain is called a ___ .', answer: 'producer', hint1: '8 letters', hint2: 'Makes its own food', hint3: 'Always a plant' },
      { id: 'bio-3.6.w2', type: 'choice', question: 'In grass → mouse → owl, the owl is the…', options: ['Producer', 'Primary consumer', 'Secondary consumer', 'Decomposer'], answer: 'Secondary consumer', hint1: 'Eats meat-eater? No, eats herbivore', hint2: 'Top of this chain', hint3: 'Second consumer in line' },
      { id: 'bio-3.6.w3', type: 'choice', question: 'Arrows in food chains show…', options: ['Where they migrate', 'Direction of energy flow', 'Order of size', 'Time of day eaten'], answer: 'Direction of energy flow', hint1: 'Prey → predator', hint2: 'Energy', hint3: 'Always one way' },
    ],
    level1: [
      { id: 'bio-3.6.1', type: 'choice', question: 'Producers get their energy from…', options: ['Each other', 'Sunlight', 'Water alone', 'Animals'], answer: 'Sunlight', hint1: 'Linked to photosynthesis', hint2: 'Trapped by chlorophyll', hint3: 'Solar' },
      { id: 'bio-3.6.2', type: 'text', question: 'Roughly what % of energy passes to the next level?', answer: '10', hint1: 'One tenth', hint2: 'Two digits', hint3: '~10%' },
      { id: 'bio-3.6.3', type: 'choice', question: 'If grasshoppers are wiped out from grass → grasshopper → frog, frog numbers will…', options: ['Increase', 'Stay the same', 'Decrease', 'Multiply rapidly'], answer: 'Decrease', hint1: 'Lost food source', hint2: 'Less energy reaches them', hint3: 'Predator without prey' },
    ],
    level2: [
      { id: 'bio-3.6.4', type: 'choice', question: 'A food web is best described as…', options: ['One chain only', 'Many chains overlapping', 'A list of producers', 'A line of decomposers'], answer: 'Many chains overlapping', hint1: 'Multiple', hint2: 'Network', hint3: 'Web shape' },
      { id: 'bio-3.6.5', type: 'text', question: 'Bacteria and fungi that break down dead matter are called ___ .', answer: 'decomposers', hint1: '11 letters', hint2: 'Recycle nutrients', hint3: 'Plural' },
      { id: 'bio-3.6.6', type: 'number', question: 'A producer holds 100,000 J. Apply the 10% rule for two more levels — how many J reach the secondary consumer?', answer: '1000', hint1: '100k → 10k → 1k', hint2: 'Apply 10% twice', hint3: 'Four digits' },
    ],
    level3: [
      { id: 'bio-3.6.7', type: 'choice', question: 'Why are there usually fewer apex predators than producers?', options: ['Predators die younger', 'Energy is lost at each level', 'Plants don\u2019t move', 'Predators are bigger and need fewer'], answer: 'Energy is lost at each level', hint1: '90% loss per step', hint2: 'Less energy higher up', hint3: 'Pyramid shape' },
      { id: 'bio-3.6.8', type: 'text', question: 'When all the species in a habitat are connected by feeding, this network is called a food ___ .', answer: 'web', hint1: '3 letters', hint2: 'Spider-style', hint3: 'Network' },
    ],
  },
};

// =====================================================================
// 3.7  PYRAMIDS OF NUMBERS AND BIOMASS
// =====================================================================
const PYRAMIDS_TOPIC = {
  id: 'bio-3.7',
  title: 'Pyramids of Numbers and Biomass',
  slug: 'pyramids-of-numbers-biomass',
  pageRef: 'Section 3 · Topic 7',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'What pyramids show',
        body:
          'A pyramid of numbers shows how many organisms are at each level of a food chain. A pyramid of biomass shows the total dry mass of all organisms at each level. Both are drawn with the producer at the bottom and the top consumer at the top. Most pyramids look like…well…pyramids: lots at the bottom, fewer as you go up.',
        check: {
          question: 'What is biomass?',
          options: ['Number of organisms', 'Total dry mass of organisms at a level', 'Type of habitat', 'Volume of an animal'],
          answer: 'Total dry mass of organisms at a level',
          explainer: 'Dry mass = the mass after water is removed; this avoids water content distorting the picture.',
        },
      },
      {
        heading: 'When numbers don\u2019t form a pyramid',
        body:
          'Pyramids of numbers can be the wrong shape! Example: oak tree → caterpillars → blue tits → fleas. ONE tree supports thousands of caterpillars (so the producer level is narrower than the primary consumer level). The pyramid is "upside down" at the bottom because a single big organism (the tree) is just one number. Pyramids of biomass fix this by using mass instead — the oak tree weighs far more than all the caterpillars combined.',
        check: {
          question: 'Why might a pyramid of NUMBERS look wrong-shaped?',
          options: ['Too many predators', 'Producer is one large organism (e.g. a tree)', 'Habitat is small', 'Animals migrate'],
          answer: 'Producer is one large organism (e.g. a tree)',
          explainer: 'One tree counts as 1; thousands of caterpillars eat from it — so the bar for producers is shorter.',
        },
      },
      {
        heading: 'Why biomass pyramids are usually upright',
        body:
          'Biomass at each level is roughly 10% of the level below — because of the energy-loss rule. So the producer level holds the most biomass, the primary consumer level holds about a tenth, and so on. That\u2019s why apex predators (e.g. tigers, eagles) need huge ranges to find enough prey to support themselves.',
        check: {
          question: 'About what percentage of biomass is passed up each level?',
          options: ['~1%', '~10%', '~50%', '~90%'],
          answer: '~10%',
          explainer: 'Energy and biomass both shrink to about a tenth at each step.',
        },
      },
    ],
    keyPoints: [
      'Pyramid of numbers = how many organisms at each level',
      'Pyramid of biomass = total dry mass at each level',
      'Numbers can look "wrong" if producer is a big single organism (tree)',
      'Biomass pyramids are almost always upright',
      '~10% of biomass passes to the next level',
      'That\u2019s why apex predators are so rare',
    ],
    workedExamples: [
      {
        problem: 'In a chain oak (1) → caterpillar (2000) → blue tit (10) → sparrowhawk (1), sketch the pyramid of NUMBERS.',
        steps: [
          { step: 1, instruction: 'Producer bar', working: '1 oak tree → very narrow bar at bottom' },
          { step: 2, instruction: 'Primary consumer', working: '2000 caterpillars → very wide bar above' },
          { step: 3, instruction: 'Secondary', working: '10 tits → narrower' },
          { step: 4, instruction: 'Tertiary', working: '1 sparrowhawk → narrowest' },
        ],
        answer: 'Pyramid is bulged in the middle (bottom is narrow because the producer is one big tree).',
        commonMistake: 'Drawing it perfectly pyramid-shaped — single-organism producers break that rule for numbers (but NOT for biomass).',
      },
    ],
    whyItWorks:
      'Biomass pyramids are a more reliable picture of how energy flows through a habitat than number pyramids. They reveal a fundamental ecological law: there\u2019s simply less and less mass available the higher up the chain you go.',
  },
  glossary: [
    { term: 'Biomass', definition: 'The total dry mass of organisms at a level in a food chain.' },
    { term: 'Pyramid of numbers', definition: 'A graph showing the number of organisms at each trophic level.' },
    { term: 'Pyramid of biomass', definition: 'A graph showing the total mass of organisms at each trophic level.' },
    { term: 'Trophic level', definition: 'A position in a food chain (producer, primary consumer, etc.).' },
    { term: 'Apex predator', definition: 'A top predator with no natural predators of its own.' },
  ],
  examQuestion: {
    prompt:
      'A meadow has 5000 grass plants, 200 grasshoppers, 30 frogs and 1 hawk. (a) Sketch the pyramid of numbers. (b) Suggest why a biomass pyramid would be a more reliable shape. (c) Explain the 10% rule. (5 marks)',
    maxMarks: 5,
    markSchemePoints: [
      'Pyramid drawn correctly with producer at bottom (5000), grasshoppers, frogs, hawk',
      'Biomass measures total mass, not number, so single big organisms don\u2019t distort the shape',
      'Biomass pyramid will be a true upright pyramid',
      'Only ~10% of energy/biomass passes to the next level',
      'Rest is lost as heat / waste / uneaten parts',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.7.w1', type: 'text', question: 'What does biomass mean?', answer: 'dry mass', hint1: 'Two words', hint2: 'No water', hint3: 'Mass after drying' },
      { id: 'bio-3.7.w2', type: 'choice', question: 'Which pyramid is almost always upright?', options: ['Pyramid of numbers', 'Pyramid of biomass', 'Pyramid of habitats', 'Pyramid of predators'], answer: 'Pyramid of biomass', hint1: 'Mass-based', hint2: 'Less variable', hint3: 'Standard shape' },
      { id: 'bio-3.7.w3', type: 'choice', question: 'About what % of biomass moves up each level?', options: ['1%', '10%', '50%', '90%'], answer: '10%', hint1: 'Same as energy', hint2: 'One tenth', hint3: 'Two digits' },
    ],
    level1: [
      { id: 'bio-3.7.1', type: 'text', question: 'A pyramid of numbers shows the ___ of organisms at each level.', answer: 'number', hint1: '6 letters', hint2: 'Count', hint3: 'How many' },
      { id: 'bio-3.7.2', type: 'choice', question: 'Why is a producer placed at the bottom?', options: ['It\u2019s the heaviest', 'It captures the sun\u2019s energy', 'It\u2019s the rarest', 'It eats most'], answer: 'It captures the sun\u2019s energy', hint1: 'Photosynthesis', hint2: 'Start of energy flow', hint3: 'Plant' },
    ],
    level2: [
      { id: 'bio-3.7.3', type: 'choice', question: 'A pyramid of numbers for oak → caterpillar → blue tit looks irregular because…', options: ['Oak is a producer', 'One tree has thousands of caterpillars', 'Tits migrate', 'Caterpillars are bigger than tits'], answer: 'One tree has thousands of caterpillars', hint1: 'Single big plant', hint2: 'Producer = 1', hint3: 'Distortion' },
      { id: 'bio-3.7.4', type: 'number', question: 'A producer has 50,000 g of biomass. Approx biomass at the secondary consumer level?', answer: '500', hint1: '50,000 → 5000 → 500', hint2: 'Apply 10% twice', hint3: 'Three digits' },
    ],
    level3: [
      { id: 'bio-3.7.5', type: 'choice', question: 'Apex predators are rare because…', options: ['They are slow to reproduce', 'There\u2019s little energy left at the top of the chain', 'They are too big for habitats', 'They die from disease'], answer: 'There\u2019s little energy left at the top of the chain', hint1: 'Energy-loss rule', hint2: '~90% lost per step', hint3: 'Pyramid shape' },
      { id: 'bio-3.7.6', type: 'text', question: 'Total dry mass at each trophic level is its ___ .', answer: 'biomass', hint1: '7 letters', hint2: 'Bio + mass', hint3: 'Used in pyramid' },
    ],
  },
};

// =====================================================================
// 3.8  COMPETITION AND ADAPTATION
// =====================================================================
const COMPETITION_TOPIC = {
  id: 'bio-3.8',
  title: 'Competition and Adaptation',
  slug: 'competition-and-adaptation',
  pageRef: 'Section 3 · Topic 8',
  difficulty: 'higher',
  diagram: null,
  learn: {
    chunks: [
      {
        heading: 'Why organisms compete',
        body:
          'No habitat has unlimited resources. Plants compete for light, water, mineral ions and space. Animals compete for food, water, mates and shelter. The "fittest" — the ones best adapted to their environment — get the most resources, survive, and pass their genes on to the next generation. This is the heart of natural selection.',
        check: {
          question: 'Plants in a forest compete most for…',
          options: ['Mates', 'Shelter', 'Light, water and minerals', 'Predators'],
          answer: 'Light, water and minerals',
          explainer: 'Plants don\u2019t move or hunt — their competition is for the resources they need to make food.',
        },
      },
      {
        heading: 'Three types of adaptation',
        body:
          'Structural adaptations are physical features (a polar bear\u2019s fur, a giraffe\u2019s neck). Behavioural adaptations are things an animal does (migration, hibernation, herding). Functional adaptations are inside the body (a camel concentrates urine to save water; cacti use a special form of photosynthesis at night). Each one increases an organism\u2019s chance of surviving in its habitat.',
        check: {
          question: 'A camel storing fat in a hump is a ___ adaptation.',
          options: ['Structural', 'Behavioural', 'Functional', 'Random'],
          answer: 'Structural',
          explainer: 'Anything physical (a body part or feature) is structural. Storing fat IN a hump is structural; using it for energy is functional.',
        },
      },
      {
        heading: 'Extremophiles',
        body:
          'Some organisms live where most can\u2019t — boiling hot springs, deep-sea vents, arctic ice. These are extremophiles. Bacteria around hydrothermal vents survive at >100 °C and use chemicals from the vent (not sunlight) for energy. They show that life can adapt to almost anything if there are enough generations of selection pressure.',
        check: {
          question: 'Bacteria living near 90 °C deep-sea vents are called…',
          options: ['Producers', 'Extremophiles', 'Predators', 'Decomposers'],
          answer: 'Extremophiles',
          explainer: 'Extreme + phile (lover) = organisms that thrive in extreme conditions.',
        },
      },
    ],
    keyPoints: [
      'Plants compete for light, water, minerals, space',
      'Animals compete for food, water, mates, shelter',
      'Three adaptations: structural, behavioural, functional',
      'Extremophiles thrive in extreme habitats (hot vents, ice)',
      'Best-adapted survive → pass genes on (natural selection)',
    ],
    workedExamples: [
      {
        problem: 'Classify each adaptation as structural, behavioural or functional: (a) Bear hibernating in winter. (b) Polar bear\u2019s thick fur. (c) Camel\u2019s very concentrated urine.',
        steps: [
          { step: 1, instruction: 'Hibernating', working: 'Something the bear DOES → behavioural' },
          { step: 2, instruction: 'Thick fur', working: 'A physical feature → structural' },
          { step: 3, instruction: 'Concentrated urine', working: 'An internal process → functional' },
        ],
        answer: '(a) behavioural, (b) structural, (c) functional.',
        commonMistake: 'Mixing up structural (body part) with functional (internal process).',
      },
    ],
    whyItWorks:
      'Adaptation is how every species fits its slot in nature. Over many generations, individuals with helpful traits leave more offspring, so those traits become more common. Eventually you get organisms that look perfectly designed — not by a designer, but by selection.',
  },
  glossary: [
    { term: 'Competition', definition: 'When organisms struggle for the same limited resources.' },
    { term: 'Adaptation', definition: 'A feature (or behaviour) that helps an organism survive in its habitat.' },
    { term: 'Structural adaptation', definition: 'A physical feature (e.g. fur, beak shape).' },
    { term: 'Behavioural adaptation', definition: 'Something an organism does (e.g. migration, hibernation).' },
    { term: 'Functional adaptation', definition: 'An internal/biochemical adaptation (e.g. concentrated urine, antifreeze in fish blood).' },
    { term: 'Extremophile', definition: 'An organism that lives in very extreme conditions (e.g. boiling vents, ice).' },
    { term: 'Natural selection', definition: 'The process where best-adapted individuals are most likely to survive and reproduce.' },
  ],
  examQuestion: {
    prompt:
      'Cacti live in deserts. Describe THREE adaptations a cactus has — one structural, one behavioural OR functional — and explain how each helps it survive. (6 marks)',
    maxMarks: 6,
    markSchemePoints: [
      'Structural: thick waxy stem',
      'Reduces water loss / stores water',
      'Structural: spines instead of leaves',
      'Reduces surface area / deters herbivores',
      'Functional: deep + spreading roots OR night-time photosynthesis',
      'Maximises water uptake / reduces water loss in heat',
    ],
  },
  practice: {
    warmUp: [
      { id: 'bio-3.8.w1', type: 'text', question: 'A feature that helps an organism survive is called an ___ .', answer: 'adaptation', hint1: '10 letters', hint2: 'Useful trait', hint3: 'Starts with a' },
      { id: 'bio-3.8.w2', type: 'choice', question: 'Which is a behavioural adaptation?', options: ['Polar bear fur', 'Bear hibernation', 'Cactus spines', 'Concentrated urine'], answer: 'Bear hibernation', hint1: 'An action, not a body part', hint2: 'Long winter sleep', hint3: 'Verb' },
      { id: 'bio-3.8.w3', type: 'choice', question: 'Plants in a forest compete for…', options: ['Mates', 'Shelter', 'Light', 'Predators'], answer: 'Light', hint1: 'For photosynthesis', hint2: 'Trees grow tall', hint3: 'Sunlight' },
    ],
    level1: [
      { id: 'bio-3.8.1', type: 'choice', question: 'Which is a STRUCTURAL adaptation?', options: ['Migration', 'Polar bear thick fur', 'Storing fat (using it)', 'Hunting at night'], answer: 'Polar bear thick fur', hint1: 'A body part', hint2: 'Physical feature', hint3: 'Insulation' },
      { id: 'bio-3.8.2', type: 'text', question: 'Animals compete for food, water, mates and ___ .', answer: 'shelter', hint1: '7 letters', hint2: 'A safe place', hint3: 'Den, nest, cave' },
      { id: 'bio-3.8.3', type: 'choice', question: 'Which is a FUNCTIONAL adaptation in a camel?', options: ['Hump', 'Long eyelashes', 'Highly concentrated urine', 'Walking in lines'], answer: 'Highly concentrated urine', hint1: 'Internal process', hint2: 'Saves water', hint3: 'Biochemical' },
    ],
    level2: [
      { id: 'bio-3.8.4', type: 'choice', question: 'Why do animals migrate?', options: ['To exercise', 'To follow food/avoid extreme weather', 'To breed', 'To hibernate'], answer: 'To follow food/avoid extreme weather', hint1: 'Behavioural adaptation', hint2: 'Move with seasons', hint3: 'Survival reason' },
      { id: 'bio-3.8.5', type: 'text', question: 'An organism that thrives in boiling hot springs is called an ___ .', answer: 'extremophile', hint1: '12 letters', hint2: 'Loves extreme habitats', hint3: 'Microbe in vents' },
    ],
    level3: [
      { id: 'bio-3.8.6', type: 'choice', question: 'Two species in the same habitat eating the exact same food will…', options: ['Live peacefully', 'Compete fiercely', 'Mate', 'Cooperate'], answer: 'Compete fiercely', hint1: 'Resource overlap', hint2: 'Niche conflict', hint3: 'Survival of fittest' },
      { id: 'bio-3.8.7', type: 'choice', question: 'Which is the BEST description of natural selection?', options: ['Organisms choose to evolve', 'Best-adapted survive and pass genes on', 'Strongest always wins', 'Random changes only'], answer: 'Best-adapted survive and pass genes on', hint1: 'Generational', hint2: 'Genes', hint3: 'Survival → reproduction' },
    ],
  },
};

// ---------- assemble the section ----------
const BASE_TOPICS = [
  PHOTOSYNTHESIS_TOPIC,
  PLANT_MINERALS_TOPIC,
  PLANT_REPRO_TOPIC,
  SEED_DISPERSAL_TOPIC,
  ECOSYSTEMS_TOPIC,
  FOOD_CHAINS_TOPIC,
  PYRAMIDS_TOPIC,
  COMPETITION_TOPIC,
];

// authoredTopics.json overrides any topic by id (for the PDF pipeline).
export const plantsAndEcosystemsTopics = BASE_TOPICS.map((t) =>
  AUTHORED_BY_ID[t.id] ? AUTHORED_BY_ID[t.id] : t,
);
